import { useCallback, useMemo } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { SearchTypeahead } from '@bambi/component-library/src/components/SearchTypeahead/SearchTypeahead';
import {
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  CurrencyDollarIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { TripRead } from 'src/common/external/bambi-api/bambiApi';
import { useDebouncedSearch } from 'src/common/useDebouncedSearch';

import { useUserRoles } from '../auth/useUserRoles';
import { TripManagementFilters } from './management/TripManagementFilters';
import {
  setShowDownloadModal,
  setShowUploadDetailsModal,
  setTripSearchTerm,
  tripSlice,
} from './trip.slice';

export function TripIndexRouteActions() {
  const dispatch = useAppDispatch();
  const { isRestrictedSupport, isRideOrderingPortalUser } = useUserRoles();

  const { selectedTripRows, tripSearchTerm } = useAppSelector(
    (state) => state.trip
  );

  const selectedTripRowsList = useMemo(() => {
    return Object.keys(selectedTripRows).filter((id) => !!selectedTripRows[id]);
  }, [selectedTripRows]);

  const handleDownload = useCallback(() => {
    dispatch(setShowDownloadModal(true));
  }, [dispatch]);

  const handleUpload = useCallback(() => {
    dispatch(setShowUploadDetailsModal(true));
  }, [dispatch]);

  const { inputValue, handleInputChange } = useDebouncedSearch(
    setTripSearchTerm,
    tripSearchTerm
  );

  const handleCancelTrips = useCallback(() => {
    const tripsToCancel = Object.values(selectedTripRows).filter((t) => !!t);
    dispatch(
      tripSlice.actions.onBulkCancelTripsStart(tripsToCancel as TripRead[])
    );
  }, [dispatch, selectedTripRows]);

  const handleBulkPriceTrips = useCallback(() => {
    const tripsToReprice = Object.values(selectedTripRows).filter((t) => !!t);
    dispatch(
      tripSlice.actions.onBulkPriceTripsStart(tripsToReprice as TripRead[])
    );
  }, [dispatch, selectedTripRows]);

  return (
    <div className="flex flex-row items-end gap-2">
      <Button
        className={selectedTripRowsList.length ? '' : 'hidden'}
        ariaHidden={!selectedTripRowsList.length}
        variant="danger"
        onClick={handleCancelTrips}
        dataTestId="bulk-cancel-trips-button"
      >
        <XCircleIcon className="mr-2 h-5 w-5" /> Cancel (
        {`${selectedTripRowsList.length}`}) Trip(s)
      </Button>
      <Button
        className={selectedTripRowsList.length ? '' : 'hidden'}
        ariaHidden={!selectedTripRowsList.length}
        variant="primary"
        onClick={handleBulkPriceTrips}
        dataTestId="bulk-price-trips-button"
      >
        <CurrencyDollarIcon className="mr-2 h-5 w-5" /> Price (
        {`${selectedTripRowsList.length}`}) Trip(s)
      </Button>
      <TripManagementFilters />
      <div className="flex flex-row gap-3">
        <SearchTypeahead
          onChange={handleInputChange}
          placeholder="Search trips"
          value={inputValue}
        />
        <Button onClick={handleDownload}>
          <CloudArrowDownIcon className="mr-2 h-5 w-5" /> Download
        </Button>
        {!isRestrictedSupport && !isRideOrderingPortalUser && (
          <Button onClick={handleUpload}>
            <CloudArrowUpIcon className="mr-2 h-5 w-5" /> Upload
          </Button>
        )}
      </div>
    </div>
  );
}
